import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSavedReport,
  deleteSavedReport,
  fetchActivityLog,
  fetchSavedReports,
  runReport,
  updateSavedReport,
  type ActivityLogQuery,
  type CreateSavedReportPayload,
  type ListSavedReportsQuery,
  type RunReportPayload,
  type UpdateSavedReportPayload,
} from '@/services/api/retailReports.api';

/** Report run is a fresh ephemeral execution — a mutation, never cached. */
export function useRunReport() {
  return useMutation({
    mutationFn: (payload: RunReportPayload) => runReport(payload),
  });
}

export function useSavedReports(query: ListSavedReportsQuery, enabled = true) {
  return useQuery({
    queryKey: ['retail-reports', 'saved', query],
    queryFn: () => fetchSavedReports(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}

export function useCreateSavedReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSavedReportPayload) => createSavedReport(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-reports', 'saved'] });
    },
  });
}

export function useUpdateSavedReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSavedReportPayload }) =>
      updateSavedReport(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-reports', 'saved'] });
    },
  });
}

export function useDeleteSavedReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSavedReport(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-reports', 'saved'] });
    },
  });
}

export function useActivityLog(query: ActivityLogQuery, enabled = true) {
  return useQuery({
    queryKey: ['retail-reports', 'activity', query],
    queryFn: () => fetchActivityLog(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}
